'use client';
import { TheWrapper } from '@hyphengroup/cms-components/components/TheWrapper';
import Tooltip from '@hyphengroup/cms-components/components/Tooltip';
import { ChevronRightIcon } from 'components/modulab/Icon';
import { addMonths } from 'date-fns';
import React, { useState, useMemo } from 'react';
import Button from '../../../Button';
import DollarCircle from '../../../icons/Dollar';
import { AmountInput } from './AmountInput';
import { CollapsibleSection } from './CollapsibleSection';
import { REPAYMENT_PERIOD_OPTIONS } from './constants';
import { DateInput } from './DateInput';
import { TRANSLATIONS } from './i18n';
import { ComponentPersonalLoanCalculator, CurrencyPrefixType } from './types';
import {
  calculateLoan,
  formatCurrency,
  type LoanInput,
  type LoanCalculationResult,
  formatMonthLabel,
  getPeriodLabel,
} from './utils';
import Calendar from '$/components/icons/Calendar';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';
import { Option } from '$/modules/material-tailwind';
import { AlphaSelect } from '$/modules/material-tailwind/AlphaSelect';

export const PersonalLoanCalculator: React.FC<
  ComponentPersonalLoanCalculator
> = ({
  defaultLoanAmount,
  defaultRepaymentPeriod,
  defaultInterestRate,
  defaultProcessingFee,
  currencyPrefix,
  eirDescription,
  showProcessingFeeField,
  interestRateLabelOverride,
  processingFeeLabelOverride,
}) => {
  /* eslint-disable tailwindcss/no-arbitrary-value */
  const context = useClientContext();
  const t = getClientTranslations(TRANSLATIONS, context);
  // CMS-1442: Flag MoneyHero HK for HK-specific interest/APR/EIR display behavior
  const isHkMarket = context.brand === 'moneyhero';

  const [loanAmount, setLoanAmount] = useState<number>(defaultLoanAmount);
  const [repaymentPeriod, setRepaymentPeriod] = useState<number>(
    defaultRepaymentPeriod,
  );
  const [interestRate, setInterestRate] = useState<number>(defaultInterestRate);
  const [processingFee, setProcessingFee] =
    useState<number>(defaultProcessingFee);
  // When processing fee field is hidden, use 0 in calculations
  const effectiveProcessingFee = showProcessingFeeField ? processingFee : 0;

  const [startDate, setStartDate] = useState<Date>(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  });
  const [result, setResult] = useState<LoanCalculationResult>(
    calculateLoan({
      loanAmount,
      processingFee: effectiveProcessingFee,
      annualInterestRate: interestRate,
      repaymentPeriodMonths: repaymentPeriod,
      startDate,
    }),
  );
  const [showAllRows, setShowAllRows] = useState<boolean>(false);
  const interestRateError =
    interestRate < 0 || interestRate > 100 ? t.interest_rate_error : '';

  // According to requirements, we always display Dollar sign for dollar currencies
  // and currency note describe which dollar currency is used
  const currencySymbol = getCurrencySymbol(currencyPrefix);
  const currencyNoteKey = getCurrencyNoteKey(currencyPrefix);
  const currencyNote = currencyNoteKey !== '' ? t[currencyNoteKey] : '';

  // Calculate loan on button click
  const handleCalculate = () => {
    const input: LoanInput = {
      loanAmount,
      processingFee: effectiveProcessingFee,
      annualInterestRate: interestRate,
      repaymentPeriodMonths: repaymentPeriod,
      startDate,
    };

    const calculationResult = calculateLoan(input);
    setResult(calculationResult);
  };

  // Check if all inputs are valid
  const isValid = useMemo(() => {
    return (
      loanAmount > 0 &&
      repaymentPeriod > 0 &&
      interestRate >= 0 &&
      interestRate <= 100 &&
      processingFee >= 0
    );
  }, [loanAmount, repaymentPeriod, interestRate, processingFee]);

  return (
    <TheWrapper type="simple">
      <div className="w-full rounded-radius/card bg-fill/card">
        {/* Container with 4 responsive sizes: sm(360px), md(768px), lg(1280px), xl(1440px) */}
        {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
        <div className="w-full md:max-w-[720px] lg:max-w-[896px]">
          {/* Two-column layout for md and above, single column for sm */}
          <div className="flex flex-col gap-gap/x p-padding/y-sm md:flex-row">
            {/* Left Column - Form Inputs */}
            {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
            <div className="flex w-full flex-col gap-spacing/gap/soft-12px md:w-[242px] md:shrink-0 lg:w-[302px] xl:w-[338px]">
              <div className="flex flex-col gap-1" hidden={!currencyNote}>
                {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
                <h2 className="font-bold text-text/primary text-[18px] leading-[22px]">
                  {t.loan_details}
                </h2>
                {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
                <p className="font-medium text-text/secondary-lighter text-[12px] leading-[16px]">
                  {currencyNote}
                </p>
              </div>

              {/* Input Form Fields */}
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-spacing/gap/y-sm">
                  {/* Loan Amount */}
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="loanAmount"
                      className="font-medium text-text/secondary text-[16px] leading-[20px]"
                    >
                      {t.loan_amount}
                    </label>
                    <AmountInput
                      id="loanAmount"
                      value={loanAmount}
                      currencySymbol={currencySymbol}
                      decimals={0}
                      onChange={setLoanAmount}
                      defaultValue={defaultLoanAmount}
                    />
                  </div>

                  {/* Processing Fee */}
                  {showProcessingFeeField && (
                    <div className="flex flex-col gap-1">
                      <label
                        htmlFor="processingFee"
                        className="font-medium text-text/secondary text-[16px] leading-[20px]"
                      >
                        {processingFeeLabelOverride || t.processing_fee}
                      </label>
                      <AmountInput
                        id="processingFee"
                        value={processingFee}
                        onChange={setProcessingFee}
                        currencySymbol={currencySymbol}
                        decimals={2}
                        defaultValue={defaultProcessingFee}
                      />
                    </div>
                  )}

                  {/* Annual Interest Rate */}
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="interestRate"
                      className="font-medium text-text/secondary text-[16px] leading-[20px]"
                    >
                      {interestRateLabelOverride || t.annual_interest_rate}
                    </label>
                    <AmountInput
                      id="interestRate"
                      value={interestRate}
                      onChange={setInterestRate}
                      currencySymbol=""
                      decimals={2}
                      error={!!interestRateError}
                      errorMessage={interestRateError}
                      defaultValue={defaultInterestRate}
                    />
                  </div>

                  {/* Repayment Period */}
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="repaymentPeriod"
                      className="font-medium text-text/secondary text-[16px] leading-[20px]"
                    >
                      {t.repayment_period}
                    </label>
                    <AlphaSelect
                      id="repaymentPeriod"
                      value={repaymentPeriod.toString()}
                      onChange={(value) => setRepaymentPeriod(Number(value))}
                      selected={(element) => {
                        if (!element) {
                          return '';
                        }

                        return (
                          <span className="alpha-form-input">
                            {element.props.children}
                          </span>
                        );
                      }}
                      required
                    >
                      {REPAYMENT_PERIOD_OPTIONS.map((months) => (
                        <Option key={months} value={months.toString()}>
                          {getPeriodLabel(months, t)}
                        </Option>
                      ))}
                    </AlphaSelect>
                  </div>

                  {/* Start Date */}
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="startDate"
                      className="font-medium text-text/secondary text-[16px] leading-[20px]"
                    >
                      {t.start_date}
                    </label>
                    <DateInput
                      id="startDate"
                      value={startDate}
                      onChange={setStartDate}
                      minDate={new Date()}
                      maxDate={addMonths(new Date(), 12)}
                    />
                  </div>

                  {/* Calculate Button */}
                  <Button
                    type="button"
                    variant="filled"
                    styleType="primary"
                    onClick={handleCalculate}
                    disabled={!isValid}
                    className="w-full"
                  >
                    {t.calculate}
                  </Button>
                </div>
              </div>
            </div>

            {/* Right Column - Results (hidden on sm, shown on md+) */}
            {result && (
              <div className="flex w-full flex-col gap-spacing/gap/x-xs md:flex-1">
                <CollapsibleSection
                  defaultOpen={true}
                  header={
                    <>
                      <DollarCircle />
                      <p className="font-semibold text-[16px] leading-[20px]">
                        {t.loan_estimate}
                      </p>
                    </>
                  }
                >
                  {/* Results Content */}
                  <div className="flex flex-col gap-spacing/gap/y-sm py-spacing/padding/y md:px-spacing/padding/x-sm">
                    {/* Monthly Repayment */}
                    <div className="flex flex-col gap-1">
                      <p className="font-bold text-text/secondary text-[14px] leading-[18px]">
                        {t.monthly_repayment}
                      </p>
                      <p className="font-bold text-highlights/blue-dark text-[18px] leading-[22px]">
                        {formatCurrency(
                          result.monthlyRepayment,
                          currencySymbol,
                        )}
                      </p>
                    </div>

                    {/* Row 1: Total Repayment & Processing Fee */}
                    <div className="flex gap-4">
                      <div className="flex flex-1 flex-col gap-1">
                        <p className="font-medium text-text/secondary-lighter text-[12px] leading-[16px]">
                          {t.total_repayment}
                        </p>
                        <p className="font-semibold text-text/primary text-[14px] leading-[18px]">
                          {formatCurrency(
                            result.totalRepayment,
                            currencySymbol,
                          )}
                        </p>
                      </div>
                      {showProcessingFeeField && (
                        <div className="flex flex-1 flex-col gap-1">
                          <p className="font-medium text-text/secondary-lighter text-[12px] leading-[16px]">
                            {processingFeeLabelOverride || t.processing_fee}
                          </p>
                          <p className="font-semibold text-text/primary text-[14px] leading-[18px]">
                            {formatCurrency(
                              result.processingFee,
                              currencySymbol,
                            )}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Row 2: Interest Rate Display - HK vs Other Markets */}
                    {isHkMarket ? (
                      // HK: Show only APR (user enters APR instead of interest rate)
                      <div className="flex flex-1 flex-col gap-1">
                        <p className="font-medium text-text/secondary-lighter text-[12px] leading-[16px]">
                          {interestRateLabelOverride || t.apr}
                        </p>
                        <div className="flex items-center gap-0.5">
                          <p className="font-semibold text-text/primary text-[14px] leading-[18px]">
                            {result.annualInterestRate.toFixed(2)}%
                          </p>
                          {!!eirDescription && (
                            <Tooltip
                              content={eirDescription}
                              placement="top"
                              infoClass="size-[--shapes-icon-size-sm]"
                            />
                          )}
                        </div>
                      </div>
                    ) : (
                      // Non-HK: Show both Annual Interest Rate and EIR
                      <div className="flex gap-4">
                        <div className="flex flex-1 flex-col gap-1">
                          <p className="font-medium text-text/secondary-lighter text-[12px] leading-[16px]">
                            {interestRateLabelOverride ||
                              t.annual_interest_rate}
                          </p>
                          <p className="font-semibold text-text/primary text-[14px] leading-[18px]">
                            {result.annualInterestRate.toFixed(2)}%
                          </p>
                        </div>
                        <div className="flex flex-1 flex-col gap-1">
                          <p className="font-medium text-text/secondary-lighter text-[12px] leading-[16px]">
                            {t.effective_interest_rate}
                          </p>
                          <div className="flex items-center gap-0.5">
                            <p className="font-semibold text-text/primary text-[14px] leading-[18px]">
                              {result.effectiveInterestRate.toFixed(2)}%
                            </p>
                            {!!eirDescription && (
                              <Tooltip
                                content={eirDescription}
                                placement="top"
                                infoClass="size-[--shapes-icon-size-sm]"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CollapsibleSection>
                <CollapsibleSection
                  defaultOpen={false}
                  header={
                    <>
                      <Calendar />
                      <span className="font-semibold text-[16px] leading-[20px]">
                        {t.payment_schedule}
                      </span>
                    </>
                  }
                >
                  {/* Payment Schedule Table */}
                  <div className="overflow-hidden">
                    <div className="overflow-x-auto">
                      <div className="flex flex-col gap-spacing/gap/y-sm py-spacing/padding/y md:px-spacing/padding/x-sm">
                        <table className="w-full border-collapse overflow-hidden rounded-lg shadow-sm text-sm">
                          <thead>
                            {/* eslint-disable-next-line tailwindcss/no-custom-classname */}
                            <tr className="mhg-alpha-typography-table-cell-strong h-table/cell-min-height-small bg-gray-100 text-text/primary">
                              <th className="border-b border-gray-200">
                                <div className="flex flex-1 items-center gap-2.5 p-spacing/gap/soft-6px">
                                  {t.month}
                                </div>
                              </th>
                              <th className="border-b border-gray-200">
                                <div className="flex flex-1 items-center gap-2.5 p-spacing/gap/soft-6px">
                                  {t.principal}
                                </div>
                              </th>
                              <th className="border-b border-gray-200">
                                <div className="flex flex-1 items-center gap-2.5 p-spacing/gap/soft-6px">
                                  {t.interest}
                                </div>
                              </th>
                              <th className="border-b border-gray-200">
                                <div className="flex flex-1 items-center gap-2.5 p-spacing/gap/soft-6px">
                                  {t.principal_balance}
                                </div>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {(showAllRows
                              ? result.paymentSchedule
                              : result.paymentSchedule.slice(0, 6)
                            ).map((payment) => (
                              <tr
                                key={payment.paymentNumber}
                                className="mhg-alpha-typography-table-cell-norm transition-colors text-text/primary hover:bg-gray-50"
                              >
                                <td className="border-b border-gray-100">
                                  <div className="flex flex-1 items-center gap-2.5 p-spacing/gap/soft-6px">
                                    {formatMonthLabel(
                                      payment.paymentDate,
                                      context.lang,
                                    )}
                                  </div>
                                </td>
                                <td className="border-b border-gray-100">
                                  <div className="flex flex-1 items-center gap-2.5 p-spacing/gap/soft-6px">
                                    {formatCurrency(
                                      payment.principal,
                                      currencySymbol,
                                    )}
                                  </div>
                                </td>
                                <td className="border-b border-gray-100">
                                  <div className="flex flex-1 items-center gap-2.5 p-spacing/gap/soft-6px">
                                    {formatCurrency(
                                      payment.interest,
                                      currencySymbol,
                                    )}
                                  </div>
                                </td>
                                <td className="border-b border-gray-100">
                                  <div className="flex flex-1 items-center gap-2.5 p-spacing/gap/soft-6px">
                                    {formatCurrency(
                                      payment.remainingPrincipal,
                                      currencySymbol,
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          {result.paymentSchedule.length > 6 &&
                            !showAllRows && (
                              <tfoot>
                                <tr>
                                  <td
                                    colSpan={4}
                                    className="border-t border-gray-200"
                                  >
                                    <div className="flex items-center gap-spacing/gap/soft-4px px-spacing/gap/soft-6px py-gap/y-sm">
                                      <button
                                        type="button"
                                        onClick={() => setShowAllRows(true)}
                                        className="flex items-center gap-spacing/gap/soft-4px"
                                        aria-expanded={false}
                                      >
                                        <span>
                                          <ChevronRightIcon size={1.8} />
                                        </span>
                                        <span className="mhg-alpha-typography-bullet-2 text-buttons/neutral-dark">
                                          {t.show_all}
                                        </span>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              </tfoot>
                            )}
                        </table>
                      </div>
                    </div>
                  </div>
                </CollapsibleSection>
              </div>
            )}
          </div>
        </div>
      </div>
    </TheWrapper>
  );
};

const getCurrencyNoteKey = (currencyPrefix?: CurrencyPrefixType) => {
  switch (currencyPrefix) {
    case 'SGD$':
      return 'currency_note_sgd';
    case 'HKD$':
      return 'currency_note_hkd';
    case 'NT$':
      return 'currency_note_ntd';
    default:
      return '';
  }
};

/// Helper to get currency symbol
/// Returns $ for dollar currencies and ₱ for Philippine Peso
const getCurrencySymbol = (currencyPrefix?: CurrencyPrefixType) => {
  switch (currencyPrefix) {
    case 'SGD$':
    case 'HKD$':
    case 'NT$':
      return '$';
    case 'PHP':
    case '₱':
      return '₱';
    default:
      return '';
  }
};
